/**
 * Remote design provider — the REAL designer, reached through the secure
 * backend proxy for ANY provider (OpenAI, Claude, Gemini). The frontend
 * builds the prompt (so schema + validator never drift); the proxy attaches
 * the server-side key for the chosen provider, forces JSON, and returns the
 * raw JSON string, which we parse into a Design JSON array.
 *
 * The browser never holds any provider key. Live calls require the provider's
 * key + SDK configured on the backend and are validated by the operator.
 */

import { buildDesignPrompt } from './promptBuilder';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const GENERATE_ENDPOINT = '/api/admin/ai-generator/generate';
const ADMIN_TOKEN_KEY = 'admin_token';

export const SUPPORTED_PROVIDERS = ['openai', 'claude', 'gemini'];

/** Extract the templates array from the model's JSON (tolerant of shape). */
export const parseDesigns = (raw) => {
  let data;
  try {
    data = typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    throw new Error('The AI returned invalid JSON.');
  }
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.templates)) return data.templates;
  if (Array.isArray(data?.designs)) return data.designs;
  if (data && typeof data === 'object') return [data];
  throw new Error('The AI response did not contain any designs.');
};

/**
 * Generate `count` concepts via the backend proxy using `provider`.
 * options: { signal, provider } — provider defaults to 'openai'.
 * Returns Promise<DesignJson[]>.
 */
export const remoteGenerateDesigns = async (brief, count = 1, { signal, provider = 'openai' } = {}) => {
  const prompt = buildDesignPrompt(brief, count);
  const res = await fetch(`${API_URL}${GENERATE_ENDPOINT}`, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY) || ''}`,
    },
    body: JSON.stringify({
      provider: SUPPORTED_PROVIDERS.includes(provider) ? provider : 'openai',
      model: prompt.model,
      count: prompt.count,
      messages: prompt.messages,
      response_format: prompt.response_format,
    }),
  });

  if (!res.ok) {
    let detail = `Generation failed (${res.status})`;
    try { detail = (await res.json())?.detail || detail; } catch { /* keep default */ }
    throw new Error(detail);
  }

  const payload = await res.json();
  return parseDesigns(payload.raw ?? payload);
};

/** Curried per-provider generators (all route through the same proxy). */
export const makeProvider = (provider) => (brief, count, options = {}) =>
  remoteGenerateDesigns(brief, count, { ...options, provider });
